/**
 * Tela mostrada quando uma rota quebra.
 *
 * POR QUE ISTO EXISTE
 * -------------------
 * Sem `errorElement`, qualquer exceção durante o render derruba a árvore
 * inteira do React e o visitante fica com uma **página em branco** — sem
 * mensagem, sem navegação, sem nada que sugira tentar de novo. Numa loja isso
 * é uma venda perdida em silêncio: a pessoa conclui que o site não existe.
 *
 * Também cobre a falha de carregar um chunk, que acontece de verdade: depois
 * de um deploy, o `index.html` em cache do visitante aponta para arquivos com
 * hash antigo que já não existem no servidor. Aí a saída certa é RECARREGAR,
 * não "tentar de novo" — por isso esse caso tem botão próprio.
 */
import { isRouteErrorResponse, useNavigate, useRouteError } from 'react-router-dom'

import { Botao } from './Botao.jsx'
import { EstadoVazio } from './EstadoVazio.jsx'

/** Chunk que sumiu do servidor depois de um deploy. */
function ehFalhaDeChunk(erro: unknown): boolean {
  const mensagem = erro instanceof Error ? erro.message : String(erro ?? '')
  return /dynamically imported module|Importing a module script failed|Loading chunk/i.test(
    mensagem,
  )
}

export function PaginaErro() {
  const erro = useRouteError()
  const navegar = useNavigate()

  // 404 de rota não é defeito: tem página própria, com o tom da loja.
  const ehNaoEncontrado = isRouteErrorResponse(erro) && erro.status === 404

  if (ehFalhaDeChunk(erro)) {
    return (
      <div className="mx-auto flex min-h-dvh max-w-lg items-center px-4">
        <EstadoVazio
          icone="bike"
          titulo="O site foi atualizado"
          descricao="Recarregue a página para pegar a versão nova."
          acao={
            <Botao variante="primaria" onClick={() => window.location.reload()}>
              Recarregar
            </Botao>
          }
        />
      </div>
    )
  }

  return (
    <div className="mx-auto flex min-h-dvh max-w-lg items-center px-4">
      <EstadoVazio
        icone="bike"
        titulo={ehNaoEncontrado ? 'Página não encontrada' : 'Algo deu errado por aqui'}
        descricao={
          ehNaoEncontrado
            ? 'O endereço pode ter mudado.'
            : 'O problema é nosso, não seu. Você pode tentar de novo ou voltar para a loja.'
        }
        acao={
          <div className="flex flex-wrap justify-center gap-3">
            {!ehNaoEncontrado && (
              <Botao variante="primaria" onClick={() => navegar(0)}>
                Tentar de novo
              </Botao>
            )}
            <Botao variante="fantasma" onClick={() => navegar('/')}>
              Ir para a loja
            </Botao>
          </div>
        }
      />
    </div>
  )
}
